import { fetchDataFromBackend } from "../api/api";
import SubOrganisationDTO from "../DTOs/subOrganisationDTO";
import logger from "../core/utils/logger";

const isSimulation = import.meta.env.VITE_BACKEND_DATA_SIMULATION === "true";

// Helper to generate simulated sub-organisations for testing
const generateSimulatedSubOrganisations = (count = 5) => {
    const subOrganisations = [];
    const statuses = ['Active', 'Inactive', 'Pending'];
    
    for (let i = 1; i <= count; i++) {
        subOrganisations.push(
            SubOrganisationDTO.fromApiResponse({
                id: i,
                organizationId: `org${Math.floor((i - 1) / 2) + 1}`,
                name: `Sub-Organisation ${i}`,
                address: `${i}00 Main Street, City ${i}`,
                email: `contact${i}@suborg${i}.com`,
                phone: `+1-555-000${i}`,
                website: `https://suborg${i}.com`,
                logo: `logo${i}.png`,
                description: `Description for Sub-Organisation ${i}`,
                status: statuses[i % statuses.length],
                isDefault: i === 1
            })
        );
    }
    
    return subOrganisations;
};

export const SubOrganisationService = {
    /**
     * Get all sub-organisations for the current organization
     * @returns {Promise<SubOrganisationDTO[]>} Array of sub-organisations
     */
    async getAllSubOrganisations() {
        try {
            if (isSimulation) {
                logger.log("Simulation mode enabled. Generating simulated sub-organisations.");
                return generateSimulatedSubOrganisations(5);
            }

            const response = await fetchDataFromBackend("/SubOrganisation/GetAll", {
                skipAuth: false,
                method: 'GET'
            });
            
            logger.log("Sub-organisations response:", response);
            return response.map(SubOrganisationDTO.fromApiResponse);
        } catch (error) {
            if (error.response) {
                if (error.response.status === 404) {
                    logger.warn("🚨 API returned 404: Sub-organisations not found.");
                    return [];
                } else {
                    logger.error("❌ API Error:", error.response.data);
                }
            } else {
                logger.error("❌ Network/Unknown Error:", error);
            }
            throw error;
        }
    },

    /**
     * Get sub-organisation by ID
     * @param {number} id - Sub-organisation ID
     * @returns {Promise<SubOrganisationDTO>} Sub-organisation data
     */
    async getSubOrganisationById(id) {
        try {
            if (isSimulation) {
                logger.log(`Simulation mode enabled. Returning simulated sub-organisation with ID ${id}.`);
                const simulated = generateSimulatedSubOrganisations(5);
                return simulated.find(sub => sub.id === id) || null;
            }

            const response = await fetchDataFromBackend(`/SubOrganisation/GetById/${id}`, {
                skipAuth: false,
                method: 'GET'
            });
            
            logger.log("Sub-organisation by ID response:", response);
            return SubOrganisationDTO.fromApiResponse(response);
        } catch (error) {
            if (error.response?.status === 404) {
                logger.warn(`🚨 Sub-organisation with ID ${id} not found.`);
                return null;
            }
            logger.error("❌ Error fetching sub-organisation by ID:", error);
            throw error;
        }
    },

    /**
     * Get current user's sub-organisations
     * @returns {Promise<SubOrganisationDTO[]>} Array of current user's sub-organisations
     */
    async getCurrentSubOrganisations() {
        try {
            if (isSimulation) {
                logger.log("Simulation mode enabled. Returning current user's simulated sub-organisations.");
                return generateSimulatedSubOrganisations(3);
            }

            const response = await fetchDataFromBackend("/SubOrganisation/GetCurrent", {
                skipAuth: false,
                method: 'GET'
            });
            
            logger.log("Current sub-organisations response:", response);
            return response.map(SubOrganisationDTO.fromApiResponse);
        } catch (error) {
            if (error.response?.status === 404) {
                logger.warn("🚨 No current sub-organisations found.");
                return [];
            }
            logger.error("❌ Error fetching current sub-organisations:", error);
            throw error;
        }
    },

    /**
     * Create a new sub-organisation
     * @param {SubOrganisationDTO} subOrganisationData - Sub-organisation data to create
     * @returns {Promise<SubOrganisationDTO>} Created sub-organisation
     */
    async createSubOrganisation(subOrganisationData) {
        try {
            if (isSimulation) {
                logger.log("Simulation mode enabled. Simulated sub-organisation creation.");
                const newSubOrg = SubOrganisationDTO.fromApiResponse({
                    ...subOrganisationData.toApiRequest(),
                    id: Math.floor(Math.random() * 1000) + 100
                });
                return newSubOrg;
            }

            // Debug: Log the data being sent to the API
            const requestData = subOrganisationData.toApiRequest();
            logger.log("🔍 DEBUG: Request data being sent to API:", requestData);
            logger.log("🔍 DEBUG: Request data JSON:", JSON.stringify(requestData, null, 2));

            const response = await fetchDataFromBackend("/SubOrganisation/Create", {
                skipAuth: false,
                method: 'POST',
                data: requestData
            });
            
            logger.log("Sub-organisation creation response:", response);
            return SubOrganisationDTO.fromApiResponse(response);
        } catch (error) {
            logger.error("❌ Error creating sub-organisation:", error);
            
            // Enhanced error logging for debugging
            if (error.response) {
                logger.error("❌ Response status:", error.response.status);
                logger.error("❌ Response data:", error.response.data);
                logger.error("❌ Response headers:", error.response.headers);
            }
            
            throw error;
        }
    },

    /**
     * Update current user's sub-organisation
     * @param {SubOrganisationDTO} subOrganisationData - Updated sub-organisation data
     * @returns {Promise<SubOrganisationDTO>} Updated sub-organisation
     */
    async updateCurrentSubOrganisation(subOrganisationData) {
        try {
            if (isSimulation) {
                logger.log("Simulation mode enabled. Simulated current sub-organisation update.");
                return SubOrganisationDTO.fromApiResponse(subOrganisationData.toApiRequest());
            }

            const response = await fetchDataFromBackend("/SubOrganisation/UpdateCurrent", {
                skipAuth: false,
                method: 'PUT',
                data: subOrganisationData.toApiRequest()
            });
            
            logger.log("Current sub-organisation update response:", response);
            return SubOrganisationDTO.fromApiResponse(response);
        } catch (error) {
            logger.error("❌ Error updating current sub-organisation:", error);
            throw error;
        }
    },

    /**
     * Update sub-organisation by ID
     * @param {number} id - Sub-organisation ID
     * @param {SubOrganisationDTO} subOrganisationData - Updated sub-organisation data
     * @returns {Promise<SubOrganisationDTO>} Updated sub-organisation
     */
    async updateSubOrganisation(id, subOrganisationData) {
        try {
            if (isSimulation) {
                logger.log(`Simulation mode enabled. Simulated sub-organisation update for ID ${id}.`);
                return SubOrganisationDTO.fromApiResponse({
                    ...subOrganisationData.toApiRequest(),
                    id: id
                });
            }

            const response = await fetchDataFromBackend(`/SubOrganisation/Update/${id}`, {
                skipAuth: false,
                method: 'PUT',
                data: subOrganisationData.toApiRequest()
            });
            
            logger.log("Sub-organisation update response:", response);
            return SubOrganisationDTO.fromApiResponse(response);
        } catch (error) {
            logger.error("❌ Error updating sub-organisation:", error);
            throw error;
        }
    },

    /**
     * Partially update sub-organisation by ID
     * @param {number} id - Sub-organisation ID
     * @param {Object} patchData - Partial data to update
     * @returns {Promise<boolean>} Success status
     */
    async patchSubOrganisation(id, patchData) {
        try {
            if (isSimulation) {
                logger.log(`Simulation mode enabled. Simulated sub-organisation patch for ID ${id}.`);
                return true;
            }

            await fetchDataFromBackend(`/SubOrganisation/Patch/${id}`, {
                skipAuth: false,
                method: 'PATCH',
                data: patchData
            });
            
            logger.log("Sub-organisation patch successful");
            return true;
        } catch (error) {
            logger.error("❌ Error patching sub-organisation:", error);
            throw error;
        }
    },

    /**
     * Delete sub-organisation by ID
     * @param {number} id - Sub-organisation ID to delete
     * @returns {Promise<boolean>} Success status
     */
    async deleteSubOrganisation(id) {
        try {
            if (isSimulation) {
                logger.log(`Simulation mode enabled. Simulated sub-organisation deletion for ID ${id}.`);
                return true;
            }

            await fetchDataFromBackend(`/SubOrganisation/Delete/${id}`, {
                skipAuth: false,
                method: 'DELETE'
            });
            
            logger.log("Sub-organisation deletion successful");
            return true;
        } catch (error) {
            if (error.response?.status === 404) {
                logger.warn(`🚨 Sub-organisation with ID ${id} not found for deletion.`);
                return false;
            }
            logger.error("❌ Error deleting sub-organisation:", error);
            throw error;
        }
    },

    /**
     * Delete sub-organisation by organization ID
     * @param {string} organizationId - Organization ID
     * @returns {Promise<boolean>} Success status
     */
    async deleteSubOrganisationByOrganizationId(organizationId) {
        try {
            if (isSimulation) {
                logger.log(`Simulation mode enabled. Simulated sub-organisation deletion for Organization ID ${organizationId}.`);
                return true;
            }

            await fetchDataFromBackend(`/SubOrganisation/DeleteByOrganizationId/${organizationId}`, {
                skipAuth: false,
                method: 'DELETE'
            });
            
            logger.log("Sub-organisation deletion by organization ID successful");
            return true;
        } catch (error) {
            if (error.response?.status === 404) {
                logger.warn(`🚨 Sub-organisation with Organization ID ${organizationId} not found for deletion.`);
                return false;
            }
            logger.error("❌ Error deleting sub-organisation by organization ID:", error);
            throw error;
        }
    }
};
